"use client";

import React, { useState, useEffect, useCallback } from 'react';
import pricingData from '@/../config/roi_calculator_pricing.json'; // Import ROI calculator pricing

// Get the monthly pricing value
const proMonthlyPrice: number = pricingData.monthlyPricing || 42; // Default to 42 if not found

export default function ROICalculatorPage() {
  const [meetingDuration, setMeetingDuration] = useState(45);
  const [hourlyRate, setHourlyRate] = useState(1);
  const [meetingsPerWeek, setMeetingsPerWeek] = useState(7);
  const [employees, setEmployees] = useState(10);

  const [currentCost, setCurrentCost] = useState(0);
  const [meetminutesCost, setMeetminutesCost] = useState(0);
  const [savings, setSavings] = useState(0);
  const [roi, setRoi] = useState(0);

  const calculateROI = useCallback(() => {
    const currentAnnualCost = hourlyRate * (meetingDuration / 60) * meetingsPerWeek * employees * 52;
    const meetminutesAnnualCost = currentAnnualCost * 0.7; // Assuming 30% savings
    const annualSavings = currentAnnualCost - meetminutesAnnualCost;
    
    // Calculate annual subscription cost from imported data (now guaranteed to be a number)
    const annualSubscription = proMonthlyPrice * 12; 
    
    const returnOnInvestment = annualSubscription > 0 ? (annualSavings / annualSubscription).toFixed(2) : Infinity;

    setCurrentCost(Math.round(currentAnnualCost));
    setMeetminutesCost(Math.round(meetminutesAnnualCost));
    setSavings(Math.round(annualSavings));
    setRoi(parseFloat(returnOnInvestment.toString())); // Ensure it handles Infinity if needed
  }, [meetingDuration, hourlyRate, meetingsPerWeek, employees]);

  useEffect(() => {
    calculateROI();
  }, [calculateROI]);

  // Generic handler for number inputs - Re-add max clamping based on digit limit
  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<number>>, min: number, max: number) => {
    const value = e.target.value;
    if (value === '') {
      setter(min); 
    } else {
      let num = parseInt(value, 10);
      if (!isNaN(num)) {
        // Clamp value between min and max (based on digit limit)
        num = Math.max(min, Math.min(max, num));
        setter(num);
      } else {
         setter(min); 
      }
    }
  };
  
  // Handler to update state but keep value as string for controlled input
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<number>>) => {
      // Ensure slider respects its own max visually, but state can exceed via input
      setter(parseInt(e.target.value, 10));
  };

  return (
    <div className="bg-[#121212] text-white py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-12 text-center">
          Visualize Your Meeting Cost Meltdown
        </h1>

        <div className="flex flex-col md:flex-row gap-12 md:gap-16">
          {/* Left Column: Sliders - Added digit limits to inputs */}
          <div className="w-full md:w-1/2 space-y-10">
            
            {/* --- Meeting Duration --- */}
            <div>
              <label htmlFor="meetingDurationSlider" className="block text-sm font-medium text-gray-400 mb-2 flex justify-between items-end">
                <div className="relative group flex-shrink-0">
                  <span className="cursor-help">
                    Average Meeting Duration (mins)
                  </span>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 text-xs font-normal text-white bg-gray-700 rounded-md shadow-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 whitespace-normal">
                    Enter the typical length of a single meeting in minutes.
                  </span>
                </div>
                {/* Inline Input Group */}
                <div className="flex items-center space-x-1.5 flex-shrink ml-2">
                  <input 
                    type="number"
                    id="meetingDurationInput" 
                    value={meetingDuration.toString()} 
                    onChange={(e) => handleNumberInputChange(e, setMeetingDuration, 1, 999)} // Pass digit limit max
                    className="bg-white/5 border border-white/10 rounded px-2 py-0.5 text-xs w-16 text-right font-semibold text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
                    aria-label="Average Meeting Duration minutes input"
                    min="1"
                    max="999" // 3 digits max
                  />
                  <span className="font-semibold text-blue-400 text-sm">mins</span> 
                  <span className="text-xs font-normal text-gray-400">({(meetingDuration / 60).toFixed(1)} hrs)</span>
                </div>
              </label>
              <input
                type="range"
                id="meetingDurationSlider"
                min="1"
                max="480"
                step="1"
                value={Math.min(meetingDuration, 480)} // Slider value still capped at slider max visually
                onChange={(e) => handleSliderChange(e, setMeetingDuration)}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
              />
            </div>

            {/* --- Hourly Rate --- */}
            <div>
              <label htmlFor="hourlyRateSlider" className="block text-sm font-medium text-gray-400 mb-2 flex justify-between items-end">
                <div className="relative group flex-shrink-0">
                  <span className="cursor-help">
                    Average Employee Hourly Rate ($)
                  </span>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 text-xs font-normal text-white bg-gray-700 rounded-md shadow-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 whitespace-normal">
                    Enter the average hourly cost (salary + benefits) for employees in meetings.
                  </span>
                </div>
                 {/* Inline Input Group */}
                <div className="flex items-center space-x-1.5 flex-shrink ml-2">
                   <span className="font-semibold text-blue-400 text-sm">$</span>
                   <input 
                    type="number"
                    id="hourlyRateInput"
                    value={hourlyRate.toString()}
                    onChange={(e) => handleNumberInputChange(e, setHourlyRate, 1, 9999)} // Pass digit limit max
                    className="bg-white/5 border border-white/10 rounded px-2 py-0.5 text-xs w-16 text-right font-semibold text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
                    aria-label="Average Employee Hourly Rate input"
                    min="1"
                    max="9999" // 4 digits max
                  />
                  <span className="font-semibold text-blue-400 text-sm">/hour</span> 
                </div>
              </label>
              <input
                type="range"
                id="hourlyRateSlider"
                min="1"
                max="200"
                step="1"
                value={Math.min(hourlyRate, 200)} // Slider value still capped at slider max visually
                onChange={(e) => handleSliderChange(e, setHourlyRate)}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
              />
            </div>
            
            {/* --- Meetings Per Week --- */}
            <div>
              <label htmlFor="meetingsPerWeekSlider" className="block text-sm font-medium text-gray-400 mb-2 flex justify-between items-end">
                <div className="relative group flex-shrink-0">
                  <span className="cursor-help">
                    Meetings per Week
                  </span>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 text-xs font-normal text-white bg-gray-700 rounded-md shadow-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 whitespace-normal">
                    Enter the average number of meetings an employee attends weekly.
                  </span>
                </div>
                 {/* Inline Input Group */}
                <div className="flex items-center space-x-1.5 flex-shrink ml-2">
                   <input 
                    type="number"
                    id="meetingsPerWeekInput"
                    value={meetingsPerWeek.toString()} 
                    onChange={(e) => handleNumberInputChange(e, setMeetingsPerWeek, 1, 999)} // Pass digit limit max
                    className="bg-white/5 border border-white/10 rounded px-2 py-0.5 text-xs w-16 text-right font-semibold text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
                    aria-label="Meetings per Week input"
                    min="1"
                    max="999" // 3 digits max
                  />
                  <span className="font-semibold text-blue-400 text-sm">/week</span> 
                </div>
              </label>
              <input
                type="range"
                id="meetingsPerWeekSlider"
                min="1"
                max="160"
                step="1"
                value={Math.min(meetingsPerWeek, 160)} // Slider value still capped at slider max visually
                onChange={(e) => handleSliderChange(e, setMeetingsPerWeek)}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
              />
            </div>

            {/* --- Employees --- */}
            <div>
              <label htmlFor="employeesSlider" className="block text-sm font-medium text-gray-400 mb-2 flex justify-between items-end">
                 <div className="relative group flex-shrink-0">
                  <span className="cursor-help">
                    Number of Employees in Meetings
                  </span>
                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 text-xs font-normal text-white bg-gray-700 rounded-md shadow-sm opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 whitespace-normal">
                    Enter the average number of employees present in a typical meeting.
                  </span>
                </div>
                 {/* Inline Input Group */}
                <div className="flex items-center space-x-1.5 flex-shrink ml-2">
                  <input 
                    type="number"
                    id="employeesInput"
                    value={employees.toString()} 
                    onChange={(e) => handleNumberInputChange(e, setEmployees, 1, 99999)} // Pass digit limit max
                    className="bg-white/5 border border-white/10 rounded px-2 py-0.5 text-xs w-16 text-right font-semibold text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
                    aria-label="Number of Employees in Meetings input"
                    min="1"
                    max="99999" // 5 digits max
                  />
                </div>
              </label>
              <input
                type="range"
                id="employeesSlider"
                min="1"
                max="5000"
                step="1"
                value={Math.min(employees, 5000)} // Slider value still capped at slider max visually
                onChange={(e) => handleSliderChange(e, setEmployees)}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-1 focus:ring-offset-[#121212]"
              />
            </div>
          </div>

          {/* Right Column: Results - No changes */}
          <div className="w-full md:w-1/2 space-y-4 md:border-l md:border-gray-700 md:pl-16">
             {/* Result 1 */}
            <div> 
              <p className="text-gray-300 font-medium text-sm mb-1">Current Meeting Cost / Year</p>
              <p className="text-3xl font-bold text-blue-400">$ {currentCost.toLocaleString()}</p>
            </div>
            {/* Result 2 - Updated Label */}
            <div>
              <p className="text-gray-300 font-medium text-sm mb-1">Est. Meeting Cost / Year (with MeetMinutes)</p>
              <p className="text-3xl font-bold text-blue-400">$ {meetminutesCost.toLocaleString()}</p>
            </div>
            {/* Result 3 */}
            <div>
              <p className="text-gray-300 font-medium text-sm mb-1">Total Savings / Year</p>
              <p className="text-3xl font-bold text-green-400">$ {savings.toLocaleString()}</p>
            </div>
            {/* Result 4 */}
            <div>
              <p className="text-gray-300 font-medium text-sm mb-1">Return on Investment (ROI)</p>
              <p className="text-3xl font-bold text-green-400">{isFinite(roi) ? `${roi}x` : 'Infinite'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
